/**
 * PassportShareCard — shareable social preview of a Social Capital passport.
 *
 * Renders a 1200×630 canvas card with brand mark, member name, SCID,
 * total points, tier, top 3 categories, and a QR of the profile URL.
 * "Share on WhatsApp / LinkedIn / X / Copy Link" buttons open pre-filled
 * share intents. The short URL is minted lazily via `/api/member/short-url`.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Share2, Copy, CheckCircle2, MessageCircle, Linkedin, Twitter,
  Download, X, Loader2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BRAND_PURPLE = "#7C3AED";
const BRAND_BLUE   = "#3B82F6";
const BRAND_GREEN  = "#10B981";


export default function PassportShareCard({ passport, open, onClose }) {
  const canvasRef = useRef(null);
  const [shortUrl, setShortUrl] = useState(null);
  const [dataUrl, setDataUrl]   = useState(null);
  const [copied, setCopied]     = useState(false);

  // Mint (or fetch cached) short URL when the modal opens.
  useEffect(() => {
    if (!open) return;
    axios.post(`${API}/member/short-url`, {}, { withCredentials: true })
      .then((r) => setShortUrl(r.data.short_url))
      .catch(() => {
        // Fallback: use the canonical profile URL directly.
        setShortUrl(window.location.origin + "/sc/passport/" + passport.scid);
      });
  }, [open, passport.scid]);

  // Draw the card whenever passport or shortUrl updates.
  useEffect(() => {
    if (!open || !shortUrl || !canvasRef.current) return;
    drawCard(canvasRef.current, passport, shortUrl)
      .then((url) => setDataUrl(url));
  }, [open, shortUrl, passport]);

  if (!open) return null;

  const shareText =
    `I've earned ${(passport.balance?.total_points || 0).toLocaleString()} ` +
    `Social Capital points recognising verified community contributions.\n` +
    `See my passport: ${shortUrl}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shortUrl);
      setCopied(true); setTimeout(() => setCopied(false), 1600);
      toast.success("Link copied");
    } catch { toast.error("Copy failed"); }
  };

  const download = () => {
    if (!dataUrl) return;
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `${passport.scid || "passport"}-card.png`;
    a.click();
  };

  const shareUrls = shortUrl ? {
    whatsapp: `https://wa.me/?text=${encodeURIComponent(shareText)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shortUrl)}`,
    twitter:  `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`,
  } : {};

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         style={{ background: "rgba(15,15,20,0.55)" }}>
      <div
        data-testid="passport-share-modal"
        className="w-full max-w-[720px] rounded-xl bg-white border p-5"
        style={{ borderColor: "#E5E7EB" }}
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="font-display text-lg" style={{ color: "#0F172A" }}>
              Share your Social Capital passport
            </h3>
            <p className="text-[12.5px]" style={{ color: "#64748B" }}>
              A verified snapshot of your community contributions.
            </p>
          </div>
          <button data-testid="share-close-btn" onClick={onClose}
                  className="w-8 h-8 rounded-full inline-flex items-center justify-center hover:bg-slate-100">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Card preview */}
        <div className="rounded-lg overflow-hidden border mb-3 relative"
             style={{ borderColor: "#E5E7EB" }}>
          {!dataUrl && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/60">
              <Loader2 className="w-5 h-5 animate-spin opacity-60" />
            </div>
          )}
          <canvas
            ref={canvasRef}
            data-testid="share-canvas"
            width={1200} height={630}
            className="w-full h-auto"
          />
        </div>

        <div className="flex flex-wrap gap-2 justify-between items-center">
          <div className="text-[12px] font-mono truncate max-w-[52%]"
               style={{ color: BRAND_BLUE }}>
            {shortUrl || "…"}
          </div>
          <div className="flex flex-wrap gap-1.5">
            <ShareBtn label="WhatsApp"
                       data-testid="share-whatsapp"
                       icon={MessageCircle}
                       color={BRAND_GREEN}
                       href={shareUrls.whatsapp} />
            <ShareBtn label="LinkedIn"
                       data-testid="share-linkedin"
                       icon={Linkedin}
                       color="#0A66C2"
                       href={shareUrls.linkedin} />
            <ShareBtn label="X"
                       data-testid="share-twitter"
                       icon={Twitter}
                       color="#0F172A"
                       href={shareUrls.twitter} />
            <button data-testid="share-copy" onClick={copy}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-medium border bg-white"
                    style={{ borderColor: "#CBD5E1", color: "#334155" }}>
              {copied ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              {copied ? "Copied" : "Copy link"}
            </button>
            <button data-testid="share-download" onClick={download} disabled={!dataUrl}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-medium border bg-white disabled:opacity-50"
                    style={{ borderColor: "#CBD5E1", color: "#334155" }}>
              <Download className="w-3.5 h-3.5" /> Download
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


function ShareBtn({ label, icon: Icon, color, href, ...rest }) {
  return (
    <a
      {...rest}
      href={href}
      target="_blank" rel="noreferrer"
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold text-white"
      style={{ background: color }}
    >
      <Icon className="w-3.5 h-3.5" /> {label}
    </a>
  );
}


// ─── Canvas drawing ─────────────────────────────────────────────────

function catLabel(slug) {
  if (!slug) return "";
  return slug.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
}


async function drawCard(canvas, passport, shortUrl) {
  const ctx = canvas.getContext("2d");
  const W = canvas.width, H = canvas.height;

  // Background gradient — brand purple → blue.
  const bg = ctx.createLinearGradient(0, 0, W, H);
  bg.addColorStop(0, "#EEF2FF");
  bg.addColorStop(1, "#F5F0FF");
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  // Card panel.
  ctx.fillStyle = "white";
  roundRect(ctx, 60, 60, W - 120, H - 120, 32);
  ctx.fill();

  // Brand mark: green leaf + blue figure.
  ctx.fillStyle = BRAND_GREEN;
  ctx.beginPath();
  ctx.moveTo(120, 165);
  ctx.bezierCurveTo(110, 105, 190, 65, 250, 100);
  ctx.bezierCurveTo(240, 155, 195, 190, 120, 165);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = BRAND_BLUE;
  ctx.beginPath(); ctx.arc(210, 108, 18, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath();
  ctx.moveTo(175, 175);
  ctx.bezierCurveTo(175, 150, 210, 138, 240, 148);
  ctx.bezierCurveTo(250, 158, 255, 180, 245, 195);
  ctx.lineTo(175, 195); ctx.closePath();
  ctx.fill();

  // Wordmark.
  ctx.fillStyle = "#0F172A";
  ctx.font = "700 34px system-ui, sans-serif";
  ctx.fillText("SocialCapital", 275, 130);
  ctx.font = "500 16px system-ui, sans-serif";
  ctx.fillStyle = BRAND_BLUE;
  ctx.fillText("SocialCapital.cc  ·  Powered by Social Pulse®", 275, 158);

  // "PASSPORT" eyebrow.
  ctx.fillStyle = BRAND_PURPLE;
  ctx.font = "600 13px ui-monospace, monospace";
  ctx.fillText("SOCIAL CAPITAL PASSPORT", 120, 260);

  // Big points number.
  const points = (passport.balance?.total_points || 0).toLocaleString();
  ctx.fillStyle = BRAND_PURPLE;
  ctx.font = "700 130px system-ui, sans-serif";
  ctx.fillText(points, 120, 380);

  ctx.font = "500 20px system-ui, sans-serif";
  ctx.fillStyle = "#334155";
  ctx.fillText("Total Social Capital points", 120, 420);

  // Member identity.
  ctx.font = "700 30px system-ui, sans-serif";
  ctx.fillStyle = "#0F172A";
  ctx.fillText(passport.display_name || passport.scid || "SocialPulse member", 120, 480);

  ctx.font = "500 18px ui-monospace, monospace";
  ctx.fillStyle = "#64748B";
  ctx.fillText(passport.scid || "", 120, 508);

  // Top 3 categories.
  const cats = (passport.by_category || []).slice(0, 3);
  cats.forEach((c, i) => {
    const y = 550 + i * 24;
    ctx.font = "600 15px system-ui, sans-serif";
    ctx.fillStyle = BRAND_GREEN;
    ctx.fillText("•", 120, y);
    ctx.fillStyle = "#334155";
    ctx.fillText(`${catLabel(c.category)}`, 140, y);
    ctx.textAlign = "right";
    ctx.fillStyle = "#0F172A";
    ctx.fillText(`${c.points.toLocaleString()} pts`, 700, y);
    ctx.textAlign = "left";
  });

  // QR box on the right.
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=260x260&data=${encodeURIComponent(shortUrl)}`;
  try {
    const img = await loadImage(qrSrc);
    ctx.fillStyle = "white";
    roundRect(ctx, 830, 220, 310, 310, 20); ctx.fill();
    ctx.strokeStyle = "#E5E7EB"; ctx.lineWidth = 1;
    roundRect(ctx, 830, 220, 310, 310, 20); ctx.stroke();
    ctx.drawImage(img, 855, 245, 260, 260);
  } catch {}
  ctx.font = "500 14px system-ui, sans-serif";
  ctx.fillStyle = "#64748B";
  ctx.textAlign = "center";
  ctx.fillText("Scan to verify passport", 985, 555);
  ctx.textAlign = "left";

  // Footer strap.
  ctx.fillStyle = BRAND_PURPLE;
  roundRect(ctx, 60, H - 90, W - 120, 60, 0); ctx.fill();
  ctx.fillStyle = "white";
  ctx.font = "600 16px system-ui, sans-serif";
  ctx.fillText("Good Deeds. Documented. Verified. Recognized. Preserved.",
                90, H - 52);

  return canvas.toDataURL("image/png");
}


function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y,     x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x,     y + h, r);
  ctx.arcTo(x,     y + h, x,     y,     r);
  ctx.arcTo(x,     y,     x + w, y,     r);
  ctx.closePath();
}


function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload  = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}
